import { Dialog, Listbox } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  agentRoute,
  worklistRoute,
  usersRoute,
  smeRoute,
  tgesmeRoute,
  businessRuleRoute,
} from "~/pages/all-routes";
import { getRoleBasedPermissions } from "~/services/auth.services";

interface IRolesModalProps {
  roles: string[];
  onClose: () => void;
}

const RolesModal = ({ roles, onClose }: IRolesModalProps) => {
  const navigate = useNavigate();
  const routes = useMemo(() => {
    return {
      "1": agentRoute.fullPath,
      "3": worklistRoute.fullPath,
      "8": tgesmeRoute.fullPath,
      "12": usersRoute.fullPath,
      "15": smeRoute.fullPath,
      "16": businessRuleRoute.fullPath,
      "18": worklistRoute.fullPath,
    } as const;
  }, []);

  const [selectedRole, setSelectedRole] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [loading, setLoading] = useState(false); // Add loading state

  useEffect(() => {
    void (async () => {
      if (selectedRole !== null) {
        setLoading(true); // Set loading to true
        sessionStorage.setItem("roleid", selectedRole?.id as string);
        localStorage.setItem("roleid", selectedRole?.id as string);
        localStorage.setItem("userRole", selectedRole?.name as string);
        const { status, data: permissions } = await getRoleBasedPermissions(
          selectedRole.id
        );
        setLoading(false); // Reset loading state
        if (status) {
          console.log(permissions);
          const targetRoute = routes[selectedRole.id as keyof typeof routes];

          if (!targetRoute) {
            console.error("No route mapped for role:", selectedRole);
            setLoading(false);
            return;
          }

          navigate({ to: targetRoute, replace: true });
          onClose();
        }
      }
    })();
  }, [navigate, routes, selectedRole]);

  return (
    <Dialog open={true} onClose={() => {}} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-sm rounded bg-white p-4 relative">
          <Dialog.Title className="text-xl font-bold mb-2">
            Select a role
          </Dialog.Title>
          <button
            onClick={onClose}
            className="absolute border-none top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            X
          </button>
          <div>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="spinner-border animate-spin border-4 border-orange-400 border-t-transparent w-5 h-5 rounded-full"></div>
              </div>
            ) : (
              <Listbox
                value={selectedRole}
                onChange={(role) => {
                  if (role !== null) {
                    setSelectedRole(role);
                  }
                }}
              >
                <div className="relative w-[200px] mt-1">
                  <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                    <span className="block truncate">
                      {selectedRole !== null ? selectedRole.name : "Select"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>
                  <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {roles.map((role, personIdx) => {
                      const id = role.split(":")[1];
                      const name = role.split(":")[0];
                      return (
                        <Listbox.Option
                          key={personIdx}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active
                                ? "bg-amber-100 text-amber-900"
                                : "text-gray-900"
                            }`
                          }
                          value={{ id, name }}
                          disabled={loading}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? "font-medium" : "font-normal"
                                }`}
                              >
                                {name}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                  <CheckIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      );
                    })}
                  </Listbox.Options>
                </div>
              </Listbox>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default RolesModal;
